import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, LogOut, RefreshCw, Trash2, ArrowLeft, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "atelier_admin_token";

const inputCls =
  "w-full bg-transparent border-b border-sand focus:border-gold outline-none py-3 text-base placeholder:text-navy/30 transition-colors";

function LoginCard({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="w-full max-w-md bg-cream border border-sand p-10"
      data-testid="admin-login-card"
    >
      <p className="text-xs uppercase tracking-[0.35em] text-gold font-medium">Private space</p>
      <h1 className="font-serif text-4xl mt-4">Welcome back, Cécile.</h1>
      <form onSubmit={submit} className="mt-10 space-y-8" data-testid="admin-login-form">
        <div>
          <label htmlFor="admin-email" className="text-xs uppercase tracking-[0.25em] text-navy/60">Email</label>
          <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="cecile@thefrenchatelier.com" className={inputCls} data-testid="admin-email-input" />
        </div>
        <div>
          <label htmlFor="admin-password" className="text-xs uppercase tracking-[0.25em] text-navy/60">Password</label>
          <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" className={inputCls} data-testid="admin-password-input" />
        </div>
        {error && <p className="text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading}
          className="btn-fill border border-navy px-8 py-3 text-sm tracking-wide w-full disabled:opacity-60 inline-flex justify-center items-center gap-2"
          data-testid="admin-login-submit">
          {loading && <Loader2 size={16} className="animate-spin" />}
          Sign in
        </button>
      </form>
    </motion.div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [enquiries, setEnquiries] = useState(null);
  const [error, setError] = useState("");

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
  }, []);

  const load = useCallback(async (t) => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${t}` } });
      setEnquiries(data);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else setError("Unable to load enquiries.");
    }
  }, [logout]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const remove = async (id) => {
    await axios.delete(`${API}/enquiries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setEnquiries((prev) => prev.filter((e) => e.id !== id));
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-cream text-navy flex items-center justify-center px-6" data-testid="admin-page">
        <div className="grain-overlay" aria-hidden="true" />
        <LoginCard onLogin={setToken} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream text-navy" data-testid="admin-dashboard">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="border-b border-sand bg-cream/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <p className="font-serif text-xl">Espace <span className="italic text-gold">Cécile</span></p>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-navy/70 hover:text-navy inline-flex items-center gap-1" data-testid="admin-back-link">
              <ArrowLeft size={15} /> Back to site
            </Link>
            <button onClick={() => load(token)} className="text-sm inline-flex items-center gap-1 border border-navy px-4 py-1.5 btn-fill" data-testid="admin-refresh-button">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={logout} className="text-sm inline-flex items-center gap-1 text-navy/70 hover:text-navy" data-testid="admin-logout-button">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <h1 className="font-serif text-4xl">Lesson enquiries</h1>
        <p className="text-navy/60 text-sm mt-2" data-testid="admin-count">
          {enquiries ? `${enquiries.length} enquir${enquiries.length > 1 ? "ies" : "y"} received` : "Loading…"}
        </p>

        {error && <p className="mt-6 text-sm text-red-700" data-testid="admin-error">{error}</p>}

        {!enquiries && !error && (
          <div className="mt-16 flex justify-center" data-testid="admin-loading">
            <Loader2 size={28} className="animate-spin text-gold" />
          </div>
        )}

        {enquiries && enquiries.length === 0 && (
          <div className="mt-16 border border-sand p-16 text-center" data-testid="admin-empty-state">
            <Inbox size={32} className="mx-auto text-gold" />
            <p className="font-serif text-2xl mt-4">No enquiries yet</p>
            <p className="text-sm text-navy/60 mt-2">Enquiries sent through the contact form will appear here.</p>
          </div>
        )}

        {enquiries && enquiries.length > 0 && (
          <div className="mt-8 overflow-x-auto border border-sand" data-testid="admin-enquiries-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-ivory text-left text-xs uppercase tracking-[0.15em] text-navy/60">
                  <th className="px-5 py-4 font-medium">Date</th>
                  <th className="px-5 py-4 font-medium">Name</th>
                  <th className="px-5 py-4 font-medium">Contact</th>
                  <th className="px-5 py-4 font-medium">Lesson</th>
                  <th className="px-5 py-4 font-medium">Level</th>
                  <th className="px-5 py-4 font-medium">Format</th>
                  <th className="px-5 py-4 font-medium">Message</th>
                  <th className="px-5 py-4" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.id} className="border-t border-sand align-top hover:bg-ivory/50 transition-colors" data-testid={`admin-enquiry-row-${e.id}`}>
                    <td className="px-5 py-4 whitespace-nowrap text-navy/60">
                      {new Date(e.created_at).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-5 py-4 font-medium whitespace-nowrap">{e.name}</td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${e.email}`} className="link-underline">{e.email}</a>
                      {e.phone && <p className="text-navy/50 mt-1">{e.phone}</p>}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">{e.service}</td>
                    <td className="px-5 py-4 whitespace-nowrap">{e.level}</td>
                    <td className="px-5 py-4 whitespace-nowrap">{e.format}</td>
                    <td className="px-5 py-4 max-w-xs text-navy/70">{e.message || "—"}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => remove(e.id)} aria-label="Delete"
                        className="text-navy/40 hover:text-red-700 transition-colors" data-testid={`admin-delete-${e.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
